import {Group, GroupDocument, GroupItem, GroupItemTimeline, GroupProject, GroupUser} from "@/pb/api/devplan/types/grouping";
import {GroupItemTableEntry} from "@/app/components/projects/grouping/groupingTypes";
import {
  FeaturesAssigner,
  FeatureTimeline,
  UnassignedUserId
} from "@/app/components/projects/grouping/gantt/assignments";
import {DocumentEntity} from "@/pb/api/devplan/types/documents";

export type GroupViewType = 'project' | 'user'

export interface GroupView {
  groupId: string;
  rootEntries: GroupItemTableEntry[];
  entryByKey: Record<string, GroupItemTableEntry>
  itemByKey: Record<string, GroupItem>
  itemTimelines: Record<string, GroupItemTimeline>
  getItemByKey: (key: string) => GroupItem | undefined
}

export class GroupState {
  public views: Record<GroupViewType, GroupView>;

  public constructor(group: Group, projectDocs: Record<string, DocumentEntity[]>) {
    const itemByKey = group.items.reduce((a, i) => ({...a, [i.key]: i}), {} as Record<string, GroupItem>)
    const [rootEntries, entryByKey] = getRootEntries(group.topLevelItems, itemByKey)
    const featuresAssigner = new FeaturesAssigner(projectDocs, getProjectsOrder(group, itemByKey))
    const projectView: GroupView = {
      groupId: group.id,
      itemByKey,
      entryByKey,
      rootEntries,
      itemTimelines: featuresAssigner.getGroupTimeline(group).itemTimelines,
      getItemByKey: (k) => itemByKey[k]
    };
    const userViewProps = createUserView(projectView, featuresAssigner.featureTimelinesByUser, projectDocs)
    const userView: GroupView = {
      ...userViewProps,
      itemTimelines: featuresAssigner.getGroupTimeline(group).itemTimelines,
    }
    this.views = {project: projectView, user: userView}
  }
}

function getRootEntries(rootIds: string[], itemByKey: Record<string, GroupItem>): [GroupItemTableEntry[], Record<string, GroupItemTableEntry>] {
  const entryById = toEntries(itemByKey)
  const root = rootIds.map(id => {
    const entry = entryById[id]
    return (!entry || entry.parentKey) ? undefined : entry
  }).filter(e => e !== undefined)
  return [root, entryById]
}

function toEntries(itemByKey: Record<string, GroupItem>) {
  const result: Record<string, GroupItemTableEntry> = {}
  Object.values(itemByKey).forEach(i => {
    const cur: GroupItemTableEntry = i.key in result
      ? result[i.key] : {key: i.key, entityId: i.entityId, type: i.type!.$case}
    result[i.key] = cur
    if (i.childItems) {
      const subRows = cur.subRows || []
      i.childItems.forEach(childKey => {
        const childItem = itemByKey[childKey]
        const child: GroupItemTableEntry = childKey in result
          ? result[childKey] : {key: childKey, entityId: childItem.entityId, type: childItem.type!.$case}
        child.parentKey = cur.key
        result[child.key] = child
        subRows.push(child)
      })
      cur.subRows = subRows
    }
  })
  return result
}

export function getProjectsOrder(group: Group, itemByKey: Record<string, GroupItem>): string[] {
  let toProcess = [...group.topLevelItems]
  const result: string[] = []
  while (toProcess.length > 0) {
    const [itemKey, ...rest] = toProcess
    toProcess = rest
    const item = itemByKey[itemKey]
    if (item?.type?.$case === "project") {
      result.push(itemKey)
    } else {
      toProcess = [...item?.childItems ?? [], ...toProcess]
    }
  }
  return result
}

function createUserView(
  projectView: GroupView,
  featureTimelinesByUser: Record<string, FeatureTimeline[]>,
  projectDocs: Record<string, DocumentEntity[]>,
) {
  const projectIdByDoc: Record<string, string> = {}
  Object.values(projectDocs).flat().forEach(d => {
    projectIdByDoc[d.id] = d.projectId
  })
  const itemByKey: Record<string, GroupItem> = {}
  Object.entries(featureTimelinesByUser).forEach(([userId, timelines]) => {
    const userItem = GroupItem.create({
      key: userId,
      entityId: userId,
      noDrag: true,
      type: {$case: "user", value: GroupUser.create({})},
      childItems: [],
    })
    itemByKey[userItem.key] = userItem
    for (const t of timelines) {
      const projectId = projectIdByDoc[t.featureId]
      if (!projectId) {
        continue
      }
      const item = GroupItem.create(
        {
          key: t.featureId,
          entityId: t.featureId,
          noDrag: true,
          type: {$case: "document", value: GroupDocument.create({})}
        }
      )
      itemByKey[item.key] = item
      const projectKey = `${projectId}-${userId}`
      const pItem = itemByKey[projectKey] || GroupItem.create({
        key: projectKey,
        entityId: projectId,
        noDrag: true,
        type: {$case: "project", value: GroupProject.create({})},
        childItems: [],
      })
      pItem.childItems.push(item.key)
      itemByKey[projectKey] = pItem
      if (!userItem.childItems.includes(pItem.key)) {
        userItem.childItems.push(pItem.key)
      }
    }
  })
  const entryByKey = toEntries(itemByKey)
  const userIds = Object.keys(featureTimelinesByUser).toSorted().filter(id => id !== UnassignedUserId);
  const rootEntries = userIds.map(uId => entryByKey[uId])
  if (Object.keys(featureTimelinesByUser).includes(UnassignedUserId)) {
    rootEntries.push(entryByKey[UnassignedUserId])
  }
  return {
    groupId: projectView.groupId,
    itemByKey,
    entryByKey,
    rootEntries,
    getItemByKey: (k: string) => itemByKey[k]
  }
}

